<template>
  <div class="lookup-table">
    <!-- Top bar of the table, filter and search -->
    <div class="control-header">
      <MultiSelectDropDown
        v-model="selectedColumns"
        :options="columns"
        :placeholder="'Select Columns'"
        icon="view_column"
      />
      <LookupTableFilters
        :options="selectedColumnsForFilter"
        :selectedFilters="selectedFilters"
        @onFilterSelected="handleFilterSelection"
      />
      <CityFilter
        :options="cities"
        :getFetchingCities="getFetchingCities"
        placeholder="Select City"
        v-model="selectedCity"
      />
      <expand />
      <SearchInput v-model="searchQuery" :placeHolder="'Search by entity id'" />
    </div>
    <div v-show="isLoading" class="progress-wrapper">
      <ProgressView />
    </div>
    <!-- Content area -->
    <div
      v-if="isDataAvailable && getLookups && getLookups.length > 0"
      class="data-grid"
    >
      <div class="data-grid-header smooth-shadow">
        <span v-for="item in getLookups[0]" :key="item.name">{{
          item.name
        }}</span>
      </div>
      <div v-if="!isLoading" class="data-grid-contents">
        <div
          v-for="(item, index) in getLookups"
          :key="index"
          :class="['data-grid-row']"
        >
          <span>
            <router-link
              :to="`/overview/${$route.params.entity_id}/${item[0].value}`"
              >{{ item[0].value }}</router-link
            >
          </span>
          <span v-for="data in item.slice(1, item.length + 1)" :key="data.id">{{
            data.value || "•"
          }}</span>
        </div>
      </div>
    </div>
    <div v-else-if="!isDataAvailable && !isLoading" class="no-data">
      <img src="@/assets/not_found.svg" />
      <p>No lookup and event data found for this entity.</p>
    </div>
    <!-- footer with pagination -->
    <div class="data-grid-footer">
      <Paginator
        :totalPages="totalPages"
        @change="setPage"
        :disabled="isLoading"
        :currentPage="currentPage"
      />
    </div>
  </div>
</template>

<script>
import SearchInput from "@/components/entities/views/SearchInput";
import Paginator from "@/components/entities/views/Paginator";
import LookupTableFilters from "@/components/entities/views/LookupTableFilters";
import CityFilter from "@/components/entities/views/CityFilter";
import { mapActions, mapGetters } from "vuex";
import moment from "@/lib/moment";

export default {
  components: {
    SearchInput,
    Paginator,
    LookupTableFilters,
    CityFilter,
  },
  data() {
    let initialPageNumber = this.$route.query.page;
    if (initialPageNumber && /^\d+$/.test(initialPageNumber))
      initialPageNumber = parseInt(initialPageNumber);
    else initialPageNumber = 1;

    return {
      selectedColumns: [],
      selectedColumnsForFilter: [],
      selectedFilters: [],
      searchQuery: "",
      isDataAvailable: false,
      currentEntity: null,
      currentPage: initialPageNumber,
      totalPages: 0,
      isLoading: true,
      lookups: [],
      columns: [],
      cities: [],
      selectedCity: null,
    };
  },
  computed: {
    ...mapGetters("Entities", ["getEntities"]),
    ...mapGetters("Map", ["getCities", "getFetchingCities"]),
    getEntityId() {
      return this.$route.params.entity_id;
    },
    getLookups() {
      const regex = new RegExp(this.searchQuery, "i");
      return this.lookups
        .map((item) => {
          const defaultItems = [
            {
              id: "id",
              name: "Entity id",
              value: item.id,
            },
            {
              id: "last_event_time",
              name: "Last Event Time",
              value: moment(new Date(item.last_event_time)).format(
                "DD/MM/YY h:mm a"
              ),
            },
          ];

          const otherItems = Object.keys(item)
            .filter((key) => key !== "id" && key !== "last_event_time")
            .map((key) => {
              return {
                id: key,
                name: key.split("__")[1] || key,
                value: item[key],
              };
            });

          return [...defaultItems, ...otherItems];
        })
        .filter((item) => regex.test(item[0].value));
    },
  },
  methods: {
    ...mapActions("Lookup", ["fetchLookupData"]),
    ...mapActions("Entities", ["fetchEntities"]),
    ...mapActions("Map", ["fetchCities"]),
    setPage(pageNumber) {
      this.currentPage = pageNumber;
      this.$router.push({
        path: this.$router.currentRoute.path,
        query: { page: pageNumber },
      });
      this.loadData();
    },
    async loadData() {
      this.isLoading = true;
      if (this.currentEntity) {
        try {
          let { data } = await this.fetchLookupData({
            entity_id: this.getEntityId,
            page_id: this.currentPage,
            columns: this.currentEntity.meta.lookup_view.event_col_name
              ? [
                  this.currentEntity.meta.lookup_view.event_col_name,
                  ...this.selectedColumns.map((item) => item.id),
                ]
              : undefined,
            filters: this.selectedCity
              ? [
                  ...this.selectedFilters,
                  {
                    colname: "__city_id",
                    comparison: "=",
                    value: this.selectedCity.col_id,
                  },
                ]
              : this.selectedFilters,
          });
          this.lookups = data.data;
          this.totalPages = data.total_pages;
          this.isLoading = false;

          if (this.lookups && this.lookups.length === 0) {
            this.isDataAvailable = false;
          } else {
            this.isDataAvailable = true;
          }
        } catch (error) {
          return Promise.reject(error);
        }
      } else {
        this.lookups = null;
        this.isDataAvailable = false;
      }
      this.isLoading = false;
    },
    handleFilterSelection(selectedFilter) {
      let filterIndex = this.selectedFilters.findIndex(
        (filter) => filter.colname === selectedFilter.colname
      );
      if (filterIndex === -1) {
        this.selectedFilters.push(selectedFilter);
      } else {
        let selectedFiltersCopy = [...this.selectedFilters];
        if (this.selectedFilters[filterIndex].value === selectedFilter.value) {
          // Remove the element:
          selectedFiltersCopy.splice(filterIndex, 1);
        } else {
          // Update the element value:
          selectedFiltersCopy[filterIndex].value = selectedFilter.value;
        }
        this.selectedFilters = selectedFiltersCopy;
      }
    },
  },
  watch: {
    selectedColumns(cols, oldCols) {
      this.selectedColumnsForFilter = [
        this.selectedColumnsForFilter[0],
        ...cols,
      ];

      // If a column for removed:
      if (cols.length < oldCols.length) {
        // Find the removed column:
        let removedCol = oldCols.find(
          (oldCol) => cols.findIndex((col) => col.id === oldCol.id) === -1
        );
        // If a filter was set on the removed column, remove that filer:
        let selectedFiltersCopy = this.selectedFilters.filter(
          (filter) => filter.colname !== removedCol.id
        );

        // If no filer was removed:
        if (selectedFiltersCopy.length === this.selectedFilters.length) {
          // No filter was removed, load the data:
          this.loadData();
        } else {
          this.selectedFilters = selectedFiltersCopy;
        }
      } else {
        // We don't trigger the loadData if a column was removed, as that will change s
        this.loadData();
      }
    },
    selectedFilters() {
      this.loadData();
    },
    selectedCity() {
      this.loadData();
    },
    getCities: {
      immediate: true,
      handler(cities) {
        // HACK: Until https://github.com/localeai/locale-polaris/issues/9 is resolved.
        this.cities = cities.map((city) => ({
          id: city.id,
          name: city.name,
          col_id: city.id,
        }));
      },
    },
  },
  async mounted() {
    this.isLoading = true;
    this.fetchCities(this.getEntityId);

    if (!(this.getEntities && this.getEntities.length > 0)) {
      await this.fetchEntities();
    }
    this.currentEntity = this.getEntities.find(
      (entity) => entity.id === this.getEntityId
    );

    if (!(this.currentEntity.meta && this.currentEntity.meta.lookup_view)) {
      this.isDataAvailable = false;
      this.isLoading = false;
      return;
    } else {
      this.isDataAvailable = true;
    }
    this.columns = this.currentEntity.meta.lookup_view
      ? this.currentEntity.meta.lookup_view.columns.map((col) => {
          return {
            id: col.col_name,
            name: col.col_name.split("__")[1] || col.col_name,
            options: col.col_options || [],
          };
        })
      : [];

    const event_col_name = this.currentEntity.meta.lookup_view.event_col_name;
    this.selectedColumnsForFilter = [
      {
        id: event_col_name,
        name: event_col_name.split("__")[1] || event_col_name,
        options: this.currentEntity.meta.lookup_view.event_col_options,
      },
    ];

    try {
      await this.loadData();
    } catch (error) {
      console.log({ error });
    }
  },
};
</script>

<style lang="scss" scoped>
.lookup-table {
  display: flex;
  flex-direction: column;
  min-height: 500px;

  .no-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px 0;
    img {
      width: 300px;
      height: auto;
    }
  }

  .progress-wrapper {
    z-index: 0;
    position: relative;
    height: 3px;
  }

  .control-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid var(--border-color);

    /* .filter-button {
      display: flex;
      flex-direction: row;
      align-items: center;
      background: transparent;
      color: var(--font-color);
      border: 0;
      padding: 13px 15px;
      border-radius: 5px;
      margin: 0 10px;
      outline: none;
      transition: 0.2s all ease-in-out;

      i {
        margin-right: 10px;

        &.caret {
          margin: 0 0 0 5px;
        }
      }

      &:hover {
        cursor: pointer;
        color: var(--color-primary);
        background: var(--color-primary-light);
      }

      &:active {
        opacity: 0.7;
      }
    } */
  }

  .data-grid {
    display: flex;
    flex-direction: column;

    .data-grid-header {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      grid-template-rows: auto;
      border-bottom: 1px solid var(--border-color);

      span {
        padding: 15px;
        font-weight: bold;
      }
    }

    .grid-layout-wrapper {
      height: 700px;
      overflow: auto;
    }

    .data-grid-contents {
      display: flex;
      flex-direction: column;

      .data-grid-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        grid-template-rows: auto;

        span {
          padding: 15px;

          &:first-child {
            color: var(--color-primary);
          }

          a {
            color: var(--color-primary);

            &:hover {
              opacity: 0.7;
            }
          }
        }

        &:nth-child(odd) {
          background: var(--color-secondary-light);
        }

        &:nth-child(even) {
          background: var(--color-secondary);
        }
      }
    }
  }
  .data-grid-footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background: var(--color-secondary);
    border-top: 2px solid var(--border-color);
  }
}
</style>
